import { ExecutionStatus } from "../enum/ExecutionStatus";

  /**
   * Interface representing the status of an action execution.
   */
  export interface ActionStatus {
    /** The execution status (running, success, error, timeout). */
    executionStatus: ExecutionStatus;
  
    /** The error object if an error occurred (null otherwise). */
    error?: Error | null;
  
    /** A flag indicating if the action is still running. */
    isRunning: boolean;
  
    /** The timeout duration in seconds (null if no timeout is set). */
    timeout?: number | null;
  }
  
  /**
   * Abstract class defining an executable action with automatic error handling.
   * @template R The result type.
   * @template P The parameter type.
   */
  export abstract class BaseAction<R, P> {
    /** The result of the action execution, or null if not completed. */
    private _result: R | null = null;

    /** The parameters required for executing the action. */
    private _params: P | null = null;

    /** The current execution status of the action. */
    private _executionStatus: ExecutionStatus = ExecutionStatus.RUNNING;

    /** The error encountered during execution, if any. */
    private _error: Error | null = null;

    /** The timeout duration in seconds (optional). */
    private _timeout: number | null = null;

    /**
     * When true, concurrent calls to execute() will coalesce: the second
     * caller awaits the already-running execution instead of starting a
     * duplicate. Subclasses opt in by setting this to true.
     */
    protected deduplicate = false;
    private _isExecuting = false;
    private _pendingExecute: Promise<void> | null = null;
  
    /**
     * Retrieves the result of the action (null if not available).
     */
    get result(): R | null {
      return this._result;
    }
  
    /**
     * Retrieves the parameters passed to the action.
     */
    get params(): P | null {
      return this._params;
    }
  
    /**
     * Returns an object representing the current execution status.
     */
    get status(): ActionStatus {
      return {
        executionStatus: this._executionStatus,
        error: this._error,
        isRunning: this._executionStatus === ExecutionStatus.RUNNING,
        timeout: this._timeout,
      };
    }
  
    /**
     * Sets the parameters for the action.
     * @param params - The parameters for execution.
     */
    setParams(params: P): void {
      this._params = params;
    }
  
    /**
     * Sets a timeout duration (in seconds) for the action.
     * @param seconds - The timeout duration.
     */
    setTimeout(seconds: number): void {
      this._timeout = seconds;
    }
  
    /**
     * Executes the action with automatic error handling and optional timeout.
     * When `deduplicate` is true, concurrent calls coalesce into a single run.
     */
    async execute(): Promise<void> {
      if (this.deduplicate) {
        if (this._isExecuting && this._pendingExecute) {
          await this._pendingExecute;
          return;
        }
        this._isExecuting = true;
        this._pendingExecute = this._executeCore().finally(() => {
          this._isExecuting = false;
          this._pendingExecute = null;
        });
        await this._pendingExecute;
        return;
      }

      await this._executeCore();
    }

    /**
     * Core execution logic (timeout + run).
     */
    private async _executeCore(): Promise<void> {
      if (this._timeout !== null) {
        await Promise.race([
          this._runWithCatch(),
          new Promise<void>((resolve) =>
            setTimeout(() => {
              this._executionStatus = ExecutionStatus.TIMEOUT;
              resolve();
            }, this._timeout! * 1000)
          ),
        ]);
      } else {
        await this._runWithCatch();
      }
    }
  
    /**
     * Executes the `run` method with automatic try-catch error handling.
     * State is reset at the start of each call so a blocked/failed execution
     * never leaves stale SUCCESS status or a stale _result from a prior call.
     */
    private async _runWithCatch(): Promise<void> {
      this._result = null;
      this._executionStatus = ExecutionStatus.RUNNING;
      this._error = null;
      try {
        const runResult = await this.run();
        if (runResult !== undefined && runResult !== null) {
          this._result = runResult;
          this._executionStatus = ExecutionStatus.SUCCESS;
        }
      } catch (error) {
        this._error = error instanceof Error ? error : new Error(String(error));
        this._executionStatus = ExecutionStatus.ERROR;
        console.error(`[${this.constructor.name}] Unhandled error in run()`, this._error);
      } finally {
        // Consume params after each execution so a second execute() without
        // setParams() throws rather than silently re-running with stale data.
        this._params = null;
      }
    }
  
    /**
     * The main execution logic to be implemented by subclasses.
     */
    protected abstract run(): Promise<R | null>;
  }